import { useEffect, useState } from "react";
import {
  CommandArg,
  GetConfigResult,
  Intent,
  Notifications,
  NotificationType,
  SetConfigParams,
  SetConfigResult,
} from "./src/DataWedgeApi.types";

import Module from "./src/DataWedgeApiModule";
import { EventSubscription } from "expo-notifications";
import { applicationId } from "expo-application";

export type { Intent };

export async function getActiveProfile(): Promise<null | string> {
  return (await runQueryCommand<string>("GET_ACTIVE_PROFILE")) ?? null;
}

export async function getScannerStatus(): Promise<null | string> {
  return (
    (await runQueryCommand<string>(
      "GET_SCANNER_STATUS",
      undefined,
      "SCANNER_STATUS"
    )) ?? null
  );
}

export async function getProfilesList(): Promise<null | string[]> {
  return (await runQueryCommand<string[]>("GET_PROFILES_LIST")) ?? null;
}

export function setConfig(config: SetConfigParams): Promise<SetConfigResult> {
  return runCommand("com.symbol.datawedge.api.SET_CONFIG", {
    PROFILE_NAME: {
      type: "string",
      value: config.profileName,
    },
    ...(config.configMode && {
      CONFIG_MODE: {
        type: "string",
        value: config.configMode,
      },
    }),
    ...(config.profileEnabled !== undefined && {
      PROFILE_ENABLED: {
        type: "string",
        value: config.profileEnabled.toString(),
      },
    }),
    APP_LIST: {
      type: "parcelable_array",
      value: config.appList.map((appConfig) => ({
        PACKAGE_NAME: {
          type: "string",
          value: appConfig.packageName,
        },
        ACTIVITY_LIST: {
          type: "string_array",
          value: appConfig.activityList,
        },
      })),
    },
    PLUGIN_CONFIG: {
      type: "parcelable_arraylist",
      value: (config.pluginConfig ?? []).map((pluginConfig) => ({
        PLUGIN_NAME: {
          type: "string",
          value: pluginConfig.name,
        },
        ...(pluginConfig.resetConfig !== undefined && {
          RESET_CONFIG: {
            type: "string",
            value: pluginConfig.resetConfig.toString(),
          },
        }),
        PARAM_LIST: {
          type: "bundle",
          value:
            pluginConfig.name === "KEYSTROKE"
              ? {
                  ...(pluginConfig.paramList.outputEnabled !== undefined && {
                    keystroke_output_enabled: {
                      type: "string",
                      value: pluginConfig.paramList.outputEnabled.toString(),
                    },
                  }),
                }
              : {
                  ...(pluginConfig.paramList.outputEnabled !== undefined && {
                    intent_output_enabled: {
                      type: "string",
                      value: pluginConfig.paramList.outputEnabled.toString(),
                    },
                  }),
                  ...(pluginConfig.paramList.intentAction && {
                    intent_action: {
                      type: "string",
                      value: pluginConfig.paramList.intentAction,
                    },
                  }),
                  ...(pluginConfig.paramList.intentCategory && {
                    intent_category: {
                      type: "string",
                      value: pluginConfig.paramList.intentCategory,
                    },
                  }),
                  ...(pluginConfig.paramList.intentDelivery !== undefined && {
                    intent_delivery: {
                      type: "int",
                      value:
                        pluginConfig.paramList.intentDelivery ===
                        "start_activity"
                          ? 0
                          : pluginConfig.paramList.intentDelivery ===
                              "start_service"
                            ? 1
                            : 2,
                    },
                  }),
                },
        },
      })),
    },
  });
}

export function getConfig(
  profileName: string
): Promise<GetConfigResult | null | undefined> {
  return runQueryCommand("GET_CONFIG", {
    type: "string",
    value: profileName,
  });
}

const getIntent = (): null | Intent =>
  Module?.getInitialIntent() ?? null;

export const subscribeIntents = (
  callback: (intent: Intent) => void
): (() => void) => {
  const subscription = Module?.addListener("onIntentReceived", ({ intent }) => {
    callback(intent);
  });
  return () => subscription?.remove?.();
};

const notifications: {
  [k in keyof Notifications]?: {
    callbacks: Set<(data: Notifications[k]) => void>;
    promise: Promise<void>;
  };
} = {};
export async function subscribeNotifications<Type extends NotificationType>(
  notificationType: Type,
  callback: (data: Notifications[Type]) => void
): Promise<() => void> {
  const notification = (notifications[notificationType] ??= {
    callbacks: new Set(),
    promise: Promise.resolve(),
  });

  let unregister: undefined | EventSubscription;

  notification.callbacks.add(callback);
  if (notification.callbacks.size === 1) {
    unregister = Module?.addListener("onCommandResult", ({ intent }) => {
      if (intent?.action === "com.symbol.datawedge.api.NOTIFICATION_ACTION") {
        console.log("NOTIFICATION", intent);
        let notification =
          intent.extras?.["com.symbol.datawedge.api.NOTIFICATION"];
        if (
          typeof notification === "object" &&
          notification["NOTIFICATION_TYPE"] === notificationType
        ) {
          for (const callback of notification.callbacks) {
            callback(notification);
          }
        }
      }
    });
    notification.promise = notification.promise.then(async () => {
      await Module?.sendCommand({
        "com.symbol.datawedge.api.REGISTER_FOR_NOTIFICATION": {
          type: "bundle",
          value: {
            "com.symbol.datawedge.api.APPLICATION_NAME": {
              type: "string",
              value: applicationId!,
            },
            "com.symbol.datawedge.api.NOTIFICATION_TYPE": {
              type: "string",
              value: notificationType,
            },
          },
        },
      });
    });
  }

  return notification.promise.then(() => () => {
    notification.callbacks.delete(callback);
    if (notification.callbacks.size === 0) {
      unregister?.remove?.();
      notification.promise = notification.promise.then(async () => {
        await Module?.sendCommand({
          "com.symbol.datawedge.api.UNREGISTER_FOR_NOTIFICATION": {
            type: "bundle",
            value: {
              "com.symbol.datawedge.api.APPLICATION_NAME": {
                type: "string",
                value: applicationId!,
              },
              "com.symbol.datawedge.api.NOTIFICATION_TYPE": {
                type: "string",
                value: notificationType,
              },
            },
          },
        });
      });
    }
  });
}

const useActiveProfile = () => {
  const [activeProfile, setActiveProfile] = useState<
    undefined | null | string
  >();

  useEffect(() => {
    const unregisterPromise = subscribeNotifications(
      "PROFILE_SWITCH",
      ({ PROFILE_NAME, PROFILE_ENABLED }) =>
        setActiveProfile(
          PROFILE_ENABLED === undefined || PROFILE_ENABLED
            ? (PROFILE_NAME ?? null)
            : null
        )
    );

    unregisterPromise.then(async () => {
      setActiveProfile(await getActiveProfile());
    });

    return () => {
      unregisterPromise.then((unregister) => {
        unregister();
      });
    };
  }, [setActiveProfile]);

  return activeProfile;
};

const useScannerStatus = () => {
  const [status, setStatus] = useState<undefined | null | string>();

  useEffect(() => {
    const unregisterPromise = subscribeNotifications(
      "SCANNER_STATUS",
      ({ STATUS }) => setStatus(STATUS)
    );

    unregisterPromise.then(async () => {
      setStatus(await getScannerStatus());
    });

    return () => {
      unregisterPromise.then((unregister) => {
        unregister();
      });
    };
  }, [setStatus]);

  return status;
};

let nextCommandId = 0;

function runCommand<T extends Record<string, any>>(
  commandName: string,
  command: Record<string, CommandArg>
): Promise<T> {
  const commandId = `storesprint:${nextCommandId}`;
  nextCommandId += 1;

  return new Promise((resolve, reject) => {
    const subscription = Module?.addListener(
      "onCommandResult",
      ({ intent }) => {
        if (
          intent?.extras?.["COMMAND"] === commandName &&
          intent?.extras?.["COMMAND_IDENTIFIER"] === commandId
        ) {
          resolve(intent.extras as T);
          subscription?.remove();
        }
      }
    );

    try {
      Module?.sendCommand({
        [commandName]: {
          type: "bundle",
          value: command,
        },
        SEND_RESULT: {
          type: "string",
          value: "true",
        },
        COMMAND_IDENTIFIER: {
          type: "string",
          value: commandId,
        },
      });
    } catch (e) {
      subscription?.remove();
      reject(e);
    }
  });
}

function runQueryCommand<T>(
  query: string,
  data?: CommandArg,
  responseKey?: string
): Promise<undefined | null | T> {
  return new Promise((resolve, reject) => {
    const subscription = Module?.addListener(
      "onCommandResult",
      ({ intent }) => {
        const resultKey = `com.symbol.datawedge.api.RESULT_${responseKey ?? query}`;
        if (resultKey in (intent?.extras ?? {})) {
          resolve(intent?.extras?.[resultKey]);
          subscription?.remove();
        }
      }
    );

    try {
      Module?.sendCommand({
        [`com.symbol.datawedge.api.${query}`]: data ?? {
          type: "string",
          value: "",
        },
      });
    } catch (e) {
      subscription?.remove();
      reject(e);
    }
  });
}

export { Module };
